import {useCallback, useRef, useState} from 'react'

const MATRIX_AUTH_WORKER_URL = 'https://batesky-matrix-auth.attps.workers.dev'
const MATRIX_WORKER_SECRET =
  'c20db6ed1fc9b7b2af518b46af7c2a0b49e3d9a1138d6aa7b4ca9ec7d9924121'

/**
 * Check which DIDs are registered on the Matrix homeserver.
 * Returns a Set of registered DIDs for fast lookup.
 */
export function useCheckMatrixUsers() {
  const [registeredDids, setRegisteredDids] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState(false)
  const cacheRef = useRef<Map<string, boolean>>(new Map())

  const checkUsers = useCallback(async (dids: string[]) => {
    // Filter to only unchecked DIDs
    const unchecked = dids.filter(did => !cacheRef.current.has(did))
    if (unchecked.length === 0) {
      // All cached, just return current state
      return
    }

    setIsLoading(true)
    try {
      const response = await fetch(`${MATRIX_AUTH_WORKER_URL}/check-users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${MATRIX_WORKER_SECRET}`,
        },
        body: JSON.stringify({dids: unchecked}),
      })

      if (response.ok) {
        const data = (await response.json()) as {registered: string[]}
        const registeredSet = new Set(data.registered)

        // Update cache
        for (const did of unchecked) {
          cacheRef.current.set(did, registeredSet.has(did))
        }

        // Rebuild full set from cache
        const allRegistered = new Set<string>()
        for (const [did, isRegistered] of cacheRef.current) {
          if (isRegistered) allRegistered.add(did)
        }
        setRegisteredDids(allRegistered)
      }
    } catch (error) {
      console.error('Failed to check Matrix users:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {registeredDids, checkUsers, isLoading}
}
